import { useEffect, useRef, useState } from 'react';
import { formatBytes } from '../format.ts';

interface DownloadItem {
  kind: 'original' | 'full';
  filename: string;
  bytes: number;
  label: string;
}

interface Props {
  versionId: string | null;
}

/**
 * Download the clip on screen, as the master it was uploaded as or as the MP4 everyone is
 * watching. What is on offer is asked for when the menu opens rather than carried in the
 * version: sizes change when the proxy job finishes, and a review page holds its versions for
 * hours.
 */
export function DownloadMenu({ versionId }: Props): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<DownloadItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);

  // Whatever is on offer belongs to the clip that was on screen when it was asked for.
  useEffect(() => {
    setOpen(false);
    setItems(null);
  }, [versionId]);

  useEffect(() => {
    if (!open) return;
    const onPointer = (e: MouseEvent): void => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const toggle = (): void => {
    if (open || !versionId) {
      setOpen(false);
      return;
    }
    setOpen(true);
    setItems(null);
    setError(null);
    void fetch(`/api/v1/versions/${versionId}/downloads`, { credentials: 'same-origin' })
      .then(async (res) => {
        const body = (await res.json()) as { items?: DownloadItem[]; error?: string };
        if (!res.ok) throw new Error(body.error ?? `download list failed (${res.status})`);
        setItems(body.items ?? []);
      })
      .catch((err: unknown) => setError(err instanceof Error ? err.message : String(err)));
  };

  return (
    <div className="dlmenu" ref={rootRef}>
      <button
        type="button"
        className={open ? 'active' : ''}
        onClick={toggle}
        disabled={!versionId}
        title="Download this clip"
      >
        <svg className="icon" viewBox="0 0 24 24" aria-hidden="true">
          <path
            d="M12 3v10m0 0l-4-4m4 4l4-4"
            stroke="currentColor"
            strokeWidth="1.8"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path d="M4 19h16" stroke="currentColor" strokeWidth="1.8" fill="none" strokeLinecap="round" />
        </svg>
      </button>

      {open ? (
        <div className="dlmenu__panel">
          {items === null && !error ? <span className="dlmenu__empty">loading…</span> : null}
          {error ? <span className="dlmenu__empty">{error}</span> : null}
          {items?.length === 0 ? <span className="dlmenu__empty">Nothing to download yet.</span> : null}
          {/* A plain link, so the guest cookie rides along — an <a> cannot send a token header. */}
          {items?.map((item) => (
            <a
              key={item.kind}
              className="dlmenu__item"
              href={`/api/v1/versions/${versionId}/download?kind=${item.kind}`}
              download={item.filename}
              onClick={() => setOpen(false)}
            >
              <span>{item.kind === 'original' ? 'Original' : 'MP4'}</span>
              <span className="muted">
                {item.label} · {formatBytes(item.bytes)}
              </span>
            </a>
          ))}
        </div>
      ) : null}
    </div>
  );
}
